/**
 * Фото товара «Договора»: слайдер по всем фото модели серии группами «На модели» / «На вешалке»
 * (`MediaViewer`) и отправка в чат — подборка кладётся в поле ответа файлами с подписью
 * «название · серия N», сотрудник отправляет сам. Подборка — на модели спереди, сзади, сбоку;
 * на модели нет — одно главное фото (`GET /api/serials/{serial}/photos`). Из слайдера можно
 * отправить и открытое фото. Спека `docs/specs/2026-10-22-contract-photos-design.md`.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Send, X } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";

import { type MediaItem, MediaViewer } from "@/components/MediaViewer";
import {
  ANGLE_LABEL,
  GROUP_LABEL,
  type SerialPhotos,
  serialCaption,
  serialPhotoFile,
  serialPhotosQuery,
} from "@/lib/serialPhotos";

/** Положить текст и файлы в поле ответа чата. */
export type OnOffer = (text: string, files: File[]) => void;

async function offer(d: SerialPhotos, ids: number[], name: string, onOffer: OnOffer) {
  const chosen = ids.map((id) => d.photos.find((p) => p.id === id)).filter((p) => p !== undefined);
  const files = await Promise.all(chosen.map((p, k) => serialPhotoFile(d.serial, p, k + 1)));
  onOffer(serialCaption(name, d.serial), files);
}

const sendWhat = (d: SerialPhotos) =>
  d.send_group === "model" ? `${d.send.length} фото на модели` : d.send.length ? "главное фото" : null;

/** Кнопка на товаре: подборка фото серии — в поле ответа. */
export function SerialPhotosSend({ serial, name, onOffer }: { serial: number; name: string; onOffer: OnOffer }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await qc.fetchQuery(serialPhotosQuery(serial));
      if (d.send.length === 0) throw new Error("у модели нет фото");
      await offer(d, d.send, name, onOffer);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={run}
        onMouseEnter={() => void qc.prefetchQuery(serialPhotosQuery(serial))}
        disabled={busy}
        className="inline-flex items-center gap-1 text-[12px] text-accent-text hover:underline disabled:opacity-60"
        title="В поле ответа: фото на модели спереди, сзади, сбоку (нет на модели — главное фото) и подпись с серией"
      >
        {busy ? <Loader2 size={12} className="animate-spin" aria-hidden /> : <Send size={12} aria-hidden />}
        {busy ? "готовлю фото…" : "фото в чат"}
      </button>
      {error && <span className="text-[11.5px] text-err">{error}</span>}
    </span>
  );
}

/** Слайдер фото серии. Нет `onOffer` (вне «Чатов») — без кнопок отправки. */
export function SerialPhotosViewer({
  serial,
  name,
  fallback,
  onClose,
  onOffer,
}: {
  serial: number;
  name: string;
  /** Фото товара из договора — пока фото модели грузятся или если их не отдали. */
  fallback: string | null;
  onClose: () => void;
  onOffer?: OnOffer | undefined;
}) {
  const q = useQuery(serialPhotosQuery(serial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (q.isPending || (q.data && q.data.photos.length === 0) || q.isError) {
    if (!q.isPending && fallback)
      return <MediaViewer items={[{ kind: "photo", url: fallback, caption: `${name} · ${serial}` }]} start={0} onClose={onClose} />;
    return createPortal(
      <div className="fixed inset-0 z-[100] grid place-items-center bg-black/90 text-white" onClick={onClose} role="dialog" aria-modal="true">
        <div className="flex items-center gap-2 text-[14px]">
          {q.isPending ? (
            <>
              <Loader2 size={18} className="animate-spin" aria-hidden /> Собираю фото модели: на модели или на вешалке…
            </>
          ) : q.isError ? (
            `Фото не загрузились: ${q.error.message}`
          ) : (
            "У модели нет фото"
          )}
          <button type="button" onClick={onClose} title="Закрыть (Esc)" className="ml-3 grid size-9 place-items-center rounded-full bg-white/10 hover:bg-white/20">
            <X size={18} />
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  const d = q.data;
  const items: MediaItem[] = d.photos.map((p) => ({
    kind: "photo",
    url: p.url,
    group: GROUP_LABEL[p.group],
    caption: [GROUP_LABEL[p.group], p.angle && ANGLE_LABEL[p.angle], p.main && "главное фото"].filter(Boolean).join(" · "),
    meta: d.send.includes(p.id) ? `${name} · ${serial} · в подборке для чата` : `${name} · ${serial}`,
  }));
  const send = async (ids: number[]) => {
    if (!onOffer) return;
    setBusy(true);
    setError(null);
    try {
      await offer(d, ids, name, onOffer);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  const btn = "inline-flex h-10 items-center gap-1.5 rounded-full px-3.5 text-[13px] disabled:opacity-60";
  const what = sendWhat(d);
  return (
    <MediaViewer
      items={items}
      start={0}
      onClose={onClose}
      actions={(_, i) =>
        onOffer && (
          <>
            {error && <span className="text-[12px] text-red-300">{error}</span>}
            {d.warning && <span className="text-[12px] text-amber-300" title={d.warning}>разметка неполная</span>}
            <button type="button" disabled={busy} onClick={() => send([d.photos[i]!.id])} className={`${btn} bg-white/10 hover:bg-white/20`} title="Это фото с подписью-серией — в поле ответа">
              <Send size={15} aria-hidden /> Это фото
            </button>
            {what && (
              <button
                type="button"
                disabled={busy}
                onClick={() => send(d.send)}
                className={`${btn} bg-white font-medium text-black hover:bg-white/90`}
                title="В поле ответа: подборка фото и подпись с серией"
              >
                {busy ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Send size={15} aria-hidden />} В чат: {what}
              </button>
            )}
          </>
        )
      }
    />
  );
}
